/**
 * Prefixed ID encoding, decoding, and parsing.
 *
 * Format: `{prefix}_{base58(uuid_bytes)}`
 * Example: `exec_6ByM4qP5NH7t4DtaCHeALh`
 *
 * @module PrefixedId
 * @see docs/project/prefixed-ids.md
 */

import { encodeBase58, decodeBase58 } from './base58.js';
import { VALID_PREFIXES, type EntityPrefixValue } from './registry.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PREFIXED_RE = /^([a-z]{2,6})_([1-9A-HJ-NP-Za-km-z]+)$/;

/**
 * Whether a string is a UUID in the canonical hyphenated form.
 *
 * Callers that pass a path or query parameter straight into a `uuid` column
 * need this: Postgres rejects a value it cannot parse with `22P02` at bind
 * time, so the query fails outright instead of returning an empty result set.
 *
 * Deliberately stricter than Postgres, which also accepts the unhyphenated
 * 32-hex and brace-wrapped forms. We only ever emit the canonical form, so
 * `isUuid(x) === false` means "not one of ours", not "Postgres would reject x".
 */
export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** Convert a UUID hex string (with or without hyphens) to a 16-byte Uint8Array. */
function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/** Convert a 16-byte Uint8Array to a hyphenated UUID string. */
function bytesToUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Encode a raw UUID into a prefixed ID string. */
export function encodePrefixedId(prefix: EntityPrefixValue, uuid: string): string {
  const bytes = uuidToBytes(uuid);
  return `${prefix}_${encodeBase58(bytes)}`;
}

/** Decode a prefixed ID string back to { prefix, uuid }. Returns null on invalid input. */
export function decodePrefixedId(
  id: string
): { prefix: EntityPrefixValue; uuid: string } | null {
  const match = id.match(PREFIXED_RE);
  if (!match) return null;

  const prefix = match[1]!;
  if (!VALID_PREFIXES.has(prefix)) return null;

  const decoded = decodeBase58(match[2]!);
  if (!decoded || decoded.length < 16 || decoded.length > 17) return null;

  // Base58 decoding may produce 17 bytes for some UUIDs — strip leading zero byte
  let normalized = decoded;
  if (decoded.length === 17) {
    if (decoded[0] !== 0) return null; // corrupted input
    normalized = decoded.slice(1);
  }

  return { prefix: prefix as EntityPrefixValue, uuid: bytesToUuid(normalized) };
}

export type ParsedId =
  | { kind: 'prefixed'; prefix: EntityPrefixValue; uuid: string }
  | { kind: 'raw_uuid'; uuid: string }
  | { kind: 'invalid' };

/**
 * Parse any ID input — accepts a prefixed ID, a raw UUID, or returns invalid.
 * This is the backwards-compatible entry point used during migration.
 */
export function parseAnyId(input: string): ParsedId {
  // Try prefixed first
  const decoded = decodePrefixedId(input);
  if (decoded) return { kind: 'prefixed', ...decoded };

  // Try raw UUID
  if (UUID_RE.test(input)) return { kind: 'raw_uuid', uuid: input.toLowerCase() };

  return { kind: 'invalid' };
}

